import React, { useState, useEffect, useContext } from "react";

import * as Yup from "yup";
import { Formik, Form, Field } from "formik";
import { toast } from "react-toastify";

import clsx from "clsx";
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import CircularProgress from "@material-ui/core/CircularProgress";
import { Colorize } from "@material-ui/icons";
import { ColorBox } from 'material-ui-color';

import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, Chip } from "@material-ui/core";
import { AttachFile, CloudUploadOutlined } from "@material-ui/icons";
import { getTokens } from "../../styles/tokens";
import { usePanelStyles, ModalTitle, FormSection, FieldLabel } from "../FormFields/panel";

const useStyles = makeStyles(theme => {
	const t = getTokens(theme.palette.type, theme.palette.primary.main);
	return {
		btnWrapper: {
			position: "relative",
		},
		buttonProgress: {
			position: "absolute",
			top: "50%",
			left: "50%",
			marginTop: -12,
			marginLeft: -12,
		},
		// amostra da cor escolhida (a cor é dado da tag)
		colorAdorment: {
			width: 20,
			height: 20,
			borderRadius: 4,
			border: `1px solid ${t.line}`,
		},
		colorBox: {
			marginTop: 12,
			display: "flex",
			justifyContent: "center",
		},
		mediaUpload: {
			display: "flex",
			flexDirection: "column",
			alignItems: "center",
			gap: 2,
			padding: "18px 16px",
			textAlign: "center",
			cursor: "pointer",
			color: t.textSecondary,
			backgroundColor: t.muted,
			border: `1px dashed ${t.lineStrong}`,
			borderRadius: t.radius.sm,
			transition: `border-color ${t.motion.fast}, background-color ${t.motion.fast}`,
			"&:hover": {
				borderColor: t.brand,
				backgroundColor: t.tint.soft,
			},
			"& svg": { fontSize: 28, color: t.textMuted, marginBottom: 4 },
			"& strong": { fontSize: 13, fontWeight: 600, color: t.text },
			"& span": { fontSize: 12 },
		},
		mediaPreview: {
			marginTop: 10,
			display: "flex",
			flexWrap: "wrap",
			gap: 8,
			"& .MuiChip-root": { maxWidth: "100%" },
		},
	};
});

const TagSchema = Yup.object().shape({
	name: Yup.string()
		.min(3, "Mensagem muito curta")
		.required("Obrigatório")
});

const TagModal = ({ open, onClose, tagId, kanban }) => {
	const classes = useStyles();
	const p = usePanelStyles();
	const { user } = useContext(AuthContext);
	const [colorPickerModalOpen, setColorPickerModalOpen] = useState(false);
	const [lanes, setLanes] = useState([]);
	const [loading, setLoading] = useState(false);
	const [selectedLane, setSelectedLane] = useState(null);
	const [selectedRollbackLane, setSelectedRollbackLane] = useState(null);

	const initialState = {
		name: "",
		color: getRandomHexColor(),
		kanban: kanban || 0,
		timeLane: 0,
		nextLaneId: 0,
		greetingMessageLane: "",
		rollbackLaneId: 0,
		mediaFiles: [],
	};

	const [formData, setFormData] = useState(initialState);

	useEffect(() => {
		const fetchLanes = async () => {
			try {
				const { data } = await api.get("/tags/", {
					params: { kanban: 1, tagId },
				});
				setLanes(data.tags);
				setLoading(false);
			} catch (err) {
				toastError(err);
				setLoading(false);
			}
		};

		if (open) {
			setLoading(true);
			fetchLanes();
		}
	}, [open, tagId]);

	useEffect(() => {
		const fetchTag = async () => {
			try {
				const { data } = await api.get(`/tags/${tagId}`);
				if (data) {
					// Parsear mediaFiles se for uma string JSON
					let parsedMediaFiles = [];
					if (data.mediaFiles) {
						try {
							parsedMediaFiles = typeof data.mediaFiles === 'string' 
								? JSON.parse(data.mediaFiles) 
								: data.mediaFiles;
							
							// Garantir que os arquivos do backend tenham as propriedades corretas
							parsedMediaFiles = parsedMediaFiles.map(file => ({
								...file,
								// Se for arquivo do backend, mapear mimetype para type
								type: file.mimetype || file.type,
								// Garantir que name existe
								name: file.originalname || file.name || 'Arquivo sem nome'
							}));
						} catch (e) {
							console.log('Erro ao parsear mediaFiles:', e);
							parsedMediaFiles = [];
						}
					}
					
					setFormData(prev => ({ 
						...initialState, 
						...data, 
						mediaFiles: parsedMediaFiles 
					}));
					setSelectedLane(data.nextLaneId || null);
					setSelectedRollbackLane(data.rollbackLaneId || null);
				}
			} catch (err) {
				toastError(err);
			}
		};

		if (open && tagId) {
			fetchTag();
		} else if (open) {
			setFormData(initialState);
			setSelectedLane(null);
			setSelectedRollbackLane(null);
		}
	}, [tagId, open]);

	const handleClose = () => {
		setFormData(initialState);
		setColorPickerModalOpen(false);
		setSelectedLane(null);
		setSelectedRollbackLane(null);
		onClose();
	};

	const handleFileUpload = (event) => {
		const files = Array.from(event.target.files);
		const validFiles = files.filter(file => {
			const maxSize = 10 * 1024 * 1024; // 10MB
			const allowedTypes = [
				'image/jpeg', 'image/png', 'image/gif', 'image/webp',
				'application/pdf',
				'audio/mpeg', 'audio/wav', 'audio/ogg',
				'video/mp4', 'video/avi', 'video/mov', 'video/webm',
				'application/x-ret'
			];
			
			if (file.size > maxSize) {
				toast.error(`Arquivo ${file.name} é muito grande. Tamanho máximo: 10MB`);
				return false;
			}
			
			if (!allowedTypes.includes(file.type)) {
				toast.error(`Tipo de arquivo ${file.type} não é suportado`);
				return false;
			}
			
			return true;
		});

		if (validFiles.length > 0) {
			setFormData(prev => ({
				...prev,
				mediaFiles: [...(Array.isArray(prev.mediaFiles) ? prev.mediaFiles : []), ...validFiles]
			}));
		}
	};

	const handleRemoveFile = (index) => {
		setFormData(prev => ({
			...prev,
			mediaFiles: (Array.isArray(prev.mediaFiles) ? prev.mediaFiles : []).filter((_, i) => i !== index)
		}));
	};

	const getFileIcon = (file) => {
		// Verificar se é um arquivo do backend (tem mimetype) ou do frontend (tem type)
		const fileType = file.mimetype || file.type;
		if (!fileType) return '📎';
		
		if (fileType.startsWith('image/')) return '🖼️';
		if (fileType === 'application/pdf') return '📄';
		if (fileType === 'application/x-ret') return '📄';
		if (fileType.startsWith('audio/')) return '🎵';
		if (fileType.startsWith('video/')) return '🎥';
		return '📎';
	};

	const formatFileSize = (bytes) => {
		if (!bytes || bytes === 0) return '0 Bytes';
		const k = 1024;
		const sizes = ['Bytes', 'KB', 'MB', 'GB'];
		const i = Math.floor(Math.log(bytes) / Math.log(k));
		return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
	};

	const handleSaveTag = async values => {
		const formData = new FormData();
		
		// Adicionar campos básicos
		formData.append('name', values.name);
		formData.append('color', values.color);
		formData.append('kanban', kanban);
		formData.append('timeLane', values.timeLane || 0);
		formData.append('nextLaneId', selectedLane || 0);
		formData.append('greetingMessageLane', values.greetingMessageLane || '');
		formData.append('rollbackLaneId', selectedRollbackLane || 0);
		
		// Adicionar arquivos de mídia
		if (values.mediaFiles && Array.isArray(values.mediaFiles) && values.mediaFiles.length > 0) {
			values.mediaFiles.forEach((file, index) => {
				formData.append(`mediaFiles`, file);
			});
		}

		try {
			if (tagId) {
				await api.put(`/tags/${tagId}`, formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			} else {
				await api.post("/tags", formData, {
					headers: {
						'Content-Type': 'multipart/form-data',
					},
				});
			}
			toast.success(kanban === 0 ? i18n.t("tagModal.success") : i18n.t("tagModal.successKanban"));
			handleClose();
		} catch (err) {
			toastError(err);
		}
	};

	function getRandomHexColor() {
		// Gerar valores aleatórios para os componentes de cor
		const red = Math.floor(Math.random() * 256); // Valor entre 0 e 255
		const green = Math.floor(Math.random() * 256); // Valor entre 0 e 255
		const blue = Math.floor(Math.random() * 256); // Valor entre 0 e 255

		// Converter os componentes de cor em uma cor hexadecimal
		const hexColor = `#${red.toString(16).padStart(2, '0')}${green.toString(16).padStart(2, '0')}${blue.toString(16).padStart(2, '0')}`;

		return hexColor;
	}

	return (
		<Dialog
			open={open}
			onClose={handleClose}
			maxWidth="sm"
			fullWidth
			scroll="paper"
			classes={{ paper: p.dialogPaper }}
		>
			<ModalTitle
				title={tagId
					? kanban === 0
						? i18n.t("tagModal.title.edit")
						: i18n.t("tagModal.title.editKanban")
					: kanban === 0
						? i18n.t("tagModal.title.add")
						: i18n.t("tagModal.title.addKanban")
				}
				subtitle={kanban === 1
					? "Etapa do funil: uma coluna do Kanban e o que acontece com o atendimento nela."
					: "Nome e cor que identificam a tag nos contatos e atendimentos."}
				onClose={handleClose}
			/>
			<Formik
				initialValues={formData}
				enableReinitialize={true}
				validationSchema={TagSchema}
				onSubmit={handleSaveTag}
			>
				{({ touched, errors, isSubmitting, values, handleChange }) => (
					<Form className={p.modalForm}>
						<DialogContent className={clsx(p.modalBody, p.labelsAbove)}>
							<FormSection>
								<div className={p.grid2}>
									<Field
										as={TextField}
										label={i18n.t("tagModal.form.name")}
										name="name"
										fullWidth
										error={touched.name && Boolean(errors.name)}
										helperText={touched.name && errors.name}
										variant="outlined"
										size="small"
										autoFocus
									/>
									<Field
										as={TextField}
										label={i18n.t("tagModal.form.color")}
										name="color"
										fullWidth
										variant="outlined"
										size="small"
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<div
														style={{ backgroundColor: values.color }}
														className={classes.colorAdorment}
													/>
												</InputAdornment>
											),
											endAdornment: (
												<InputAdornment position="end">
													<IconButton
														size="small"
														aria-label="Escolher cor"
														onClick={() => setColorPickerModalOpen(!colorPickerModalOpen)}
													>
														<Colorize fontSize="small" />
													</IconButton>
												</InputAdornment>
											),
										}}
									/>
								</div>
								{colorPickerModalOpen && (
									<div className={classes.colorBox}>
										<ColorBox
											disableAlpha
											hslGradient={false}
											value={values.color}
											onChange={val => {
												handleChange({
													target: {
														name: 'color',
														value: `#${val.hex}`
													}
												});
											}}
										/>
									</div>
								)}
							</FormSection>

							{kanban === 1 && (
								<>
									<FormSection
										title="Automação da etapa"
										description="Tempo de permanência e para onde o atendimento segue ou volta."
									>
										<div className={p.grid2}>
											<Field
												as={TextField}
												label={i18n.t("tagModal.form.timeLane")}
												name="timeLane"
												fullWidth
												variant="outlined"
												size="small"
												type="number"
											/>
											<FormControl
												variant="outlined"
												size="small"
												fullWidth
											>
												<InputLabel>
													{i18n.t("tagModal.form.nextLaneId")}
												</InputLabel>
												<Select
													value={selectedLane || ''}
													onChange={(e) => setSelectedLane(e.target.value)}
													label={i18n.t("tagModal.form.nextLaneId")}
												>
													<MenuItem value="">&nbsp;</MenuItem>
													{lanes && lanes.length > 0 && lanes.map((lane) => (
														<MenuItem key={lane.id} value={lane.id}>
															{lane.name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<FormControl
												variant="outlined"
												size="small"
												fullWidth
												className={p.span2}
											>
												<InputLabel>
													{i18n.t("tagModal.form.rollbackLaneId")}
												</InputLabel>
												<Select
													value={selectedRollbackLane || ''}
													onChange={(e) => setSelectedRollbackLane(e.target.value)}
													label={i18n.t("tagModal.form.rollbackLaneId")}
												>
													<MenuItem value="">&nbsp;</MenuItem>
													{lanes && lanes.length > 0 && lanes.map((lane) => (
														<MenuItem key={lane.id} value={lane.id}>
															{lane.name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										</div>
									</FormSection>

									<FormSection
										title="Mensagem ao entrar na etapa"
										description="Enviada ao cliente quando o atendimento chega a esta coluna."
									>
										<div className={p.stack}>
											<Field
												as={TextField}
												label={i18n.t("tagModal.form.greetingMessageLane")}
												name="greetingMessageLane"
												fullWidth
												multiline
												rows={4}
												variant="outlined"
												size="small"
											/>
											<FieldLabel label={i18n.t("tagModal.form.mediaFiles")}>
												<div
													className={classes.mediaUpload}
													role="button"
													tabIndex={0}
													onClick={() => document.getElementById('media-upload').click()}
													onKeyDown={(e) => {
														if (e.key === "Enter" || e.key === " ") {
															e.preventDefault();
															document.getElementById('media-upload').click();
														}
													}}
												>
													<CloudUploadOutlined />
													<strong>Clique para anexar mídia (Imagem, PDF, Áudio, Vídeo)</strong>
													<span>Tamanho máximo: 10MB por arquivo</span>
													<input
														id="media-upload"
														type="file"
														multiple
														accept="image/*,application/pdf,audio/*,video/*"
														onChange={e => {
															const files = Array.from(e.target.files || []);
															const maxSize = 10 * 1024 * 1024; // 10MB
															const allowedTypes = [
																'image/jpeg', 'image/png', 'image/gif', 'image/webp',
																'application/pdf',
																'audio/mpeg', 'audio/wav', 'audio/ogg',
																'video/mp4', 'video/avi', 'video/mov', 'video/webm'
															];
															const validFiles = files.filter(file => {
																if (file.size > maxSize) {
																	toast.error(`Arquivo ${file.name} é muito grande. Tamanho máximo: 10MB`);
																	return false;
																}
																if (!allowedTypes.includes(file.type)) {
																	toast.error(`Tipo de arquivo ${file.type} não é suportado`);
																	return false;
																}
																return true;
															});
															if (validFiles.length > 0) {
																const current = Array.isArray(values.mediaFiles) ? values.mediaFiles : [];
																const next = [...current, ...validFiles];
																// Update Formik state to avoid reinitializing the whole form
																// and losing typed fields
																// Also reset input to allow selecting the same file again
																e.target.value = null;
																return handleChange({ target: { name: 'mediaFiles', value: next } });
															}
															e.target.value = null;
														}}
														style={{ display: 'none' }}
													/>
												</div>
												{values.mediaFiles && Array.isArray(values.mediaFiles) && values.mediaFiles.length > 0 && (
													<div className={classes.mediaPreview}>
														{values.mediaFiles.map((file, index) => (
															<Chip
																key={index}
																icon={<AttachFile />}
																label={`${getFileIcon(file)} ${file.originalname || file.name} (${formatFileSize(file.size)})`}
																onDelete={() => {
																	const current = Array.isArray(values.mediaFiles) ? values.mediaFiles : [];
																	const next = current.filter((_, i) => i !== index);
																	handleChange({ target: { name: 'mediaFiles', value: next } });
																}}
																variant="outlined"
																size="small"
															/>
														))}
													</div>
												)}
											</FieldLabel>
										</div>
									</FormSection>
								</>
							)}
						</DialogContent>
						<DialogActions>
							<Button
								onClick={handleClose}
								disabled={isSubmitting}
							>
								{i18n.t("tagModal.buttons.cancel")}
							</Button>
							<Button
								type="submit"
								color="primary"
								disabled={isSubmitting}
								variant="contained"
								className={classes.btnWrapper}
							>
								{tagId
									? i18n.t("tagModal.buttons.okEdit")
									: i18n.t("tagModal.buttons.okAdd")}
								{isSubmitting && (
									<CircularProgress
										size={24}
										className={classes.buttonProgress}
									/>
								)}
							</Button>
						</DialogActions>
					</Form>
				)}
			</Formik>
		</Dialog>
	);
};

export default TagModal;
